import React from 'react';
import { makeStyles, shorthands, Button, Subtitle1, Title1 } from '@fluentui/react-components';
import { SignOutRegular } from "@fluentui/react-icons";

import { useSignInState } from './Components/signInState';
import { useHandleLogout } from './Components/HandleLogout';
import {
  classifyWorkspaceSkuState,
  type WorkspaceSkuState,
} from './services/workspaceSubscription';
import { formatDateDDMMYYYY } from './Helpers/dateUtils';

import backgroundImage from './Assets/backgrounds/basecamphero.png';
import logo from './Assets/Logos/logo-white.png';

const useStyles = makeStyles({
    container: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // justifyContent: 'center',
        ...shorthands.gap('1rem'),
        ...shorthands.padding('2rem'),
        boxSizing: 'border-box',
        // maxWidth: '800px',
        // minWidth: '370px',
        ...shorthands.margin('auto'),
    },
    pageWrapper: {
        boxSizing: 'border-box',
        padding: '8px',
        backgroundColor: '#ECECEC',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        position: 'relative', // Allows absolutely positioned children (shade, logo)
    },
    shade: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 1, // Behind the card and logo
    },
    logoWrapper: {
        position: 'absolute',
        top: '30px',
        left: '30px',
        zIndex: 3, // Above the shade and card
        width: '150px', // Adjust as needed
        height: 'auto',
    },
    logoImage: {
        width: '150px', // adjust as needed
        height: 'auto',
    },
    loginCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '12px',
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        margin: '0 auto',
        width: '100%',
        maxWidth: '600px',
        zIndex: 2,
        position: 'relative',
        boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.2)',
    },
    heading: {
        textAlign: 'center',
    },
    buttons: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    // other styles...
});

interface WorkspaceBlockCopy {
    title: string;
    subtitle?: string;
}

/**
 * Maps a non-usable workspace SKU state to the copy shown on the full-screen
 * block screen. The blocker has no dismiss action; the only escape is Logout.
 */
const workspaceBlockCopy = (
    state: Exclude<WorkspaceSkuState, 'usable'>,
    expirationDate: string | null,
): WorkspaceBlockCopy => {
    const formattedExpiry = expirationDate ? formatDateDDMMYYYY(expirationDate) : '';
    switch (state) {
        case 'noSku':
            return {
                title: 'No active workspace subscription',
                subtitle: 'Your account is not linked to an active Clarity subscription. Speak to your administrator.',
            };
        case 'disabled':
            return {
                title: 'Workspace Subscription Inactive',
                subtitle: 'Your workspace subscription has been disabled. Speak to your administrator.',
            };
        case 'trialExpired':
            return {
                title: 'Trial Expired',
                subtitle: formattedExpiry
                    ? `Your Clarity trial ended on ${formattedExpiry}. Speak to your administrator to upgrade.`
                    : 'Your Clarity trial has ended. Speak to your administrator to upgrade.',
            };
        case 'subscriptionExpired':
            return {
                title: 'Subscription Expired',
                subtitle: formattedExpiry
                    ? `Your Clarity subscription expired on ${formattedExpiry}. Speak to your administrator to renew.`
                    : 'Your Clarity subscription has expired. Speak to your administrator to renew.',
            };
        case 'noFeatures':
            return {
                title: 'No Clarity features enabled',
                subtitle: 'Your workspace subscription does not have any Clarity features enabled. Speak to your administrator.',
            };
    }
};

const SignUpFlow = () => {
    const { signInState } = useSignInState();
    const { handleLogout } = useHandleLogout();
    const styles = useStyles();

    // Workspace-block branches run FIRST and only when the user is fully signed in.
    // App.tsx renders <SignUpFlow /> when (shouldRegister || isWorkspaceBlocked); the
    // existing hasAccount / customerInfo branches below assume the !isAuthed path,
    // so without this guard a fully-signed-in user with an expired SKU could fall
    // through to e.g. "User Account is Inactive", which would be misleading.
    const workspaceState = classifyWorkspaceSkuState(
        signInState.workspaceSubscription,
        signInState.isWorkspaceSuperAdmin,
    );
    if (signInState.isAuthed && workspaceState !== 'usable') {
        const copy = workspaceBlockCopy(
            workspaceState,
            signInState.workspaceSubscription?.skuDetails?.expirationDate ?? null,
        );
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.shade}></div>
                <div className={styles.logoWrapper}>
                    <img className={styles.logoImage} src={logo} alt="Basecamp Logo" />
                </div>
                <div className={styles.loginCard}>
                    <div className={styles.heading}>
                        <Title1>{copy.title}</Title1><p/>
                        {copy.subtitle && (<><Subtitle1>{copy.subtitle}</Subtitle1><p/></>)}
                        <Button appearance='primary' size="medium" icon={<SignOutRegular />} onClick={handleLogout}>
                            Logout
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (signInState.hasAccount && signInState.customerInfo.length === 0) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.shade}></div>
                {/* Logo is now a sibling of pageWrapperInner and absolutely positioned */}
                <div className={styles.logoWrapper}>
                    <img className={styles.logoImage} src={logo} alt="Basecamp Logo" />
                </div>
                <div className={styles.loginCard} >
                    <div className={styles.heading}>
                        <Title1>There are no active customer accounts assigned to your user</Title1><p/> 
                        <Subtitle1>Speak to the administrator for the company you are trying to access for an invite.</Subtitle1><p/> 
                        <Button appearance='primary' size="medium" icon={<SignOutRegular />} onClick={handleLogout} >
                            Logout
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    if (signInState.hasAccount) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.shade}></div>
                {/* Logo is now a sibling of pageWrapperInner and absolutely positioned */}
                <div className={styles.logoWrapper}>
                    <img className={styles.logoImage} src={logo} alt="Basecamp Logo" />
                </div>
                <div className={styles.loginCard} >
                    <div className={styles.heading}>
                        <Title1>User Account is Inactive</Title1><p/> 
                        <Button appearance='primary' size="medium" icon={<SignOutRegular />} onClick={handleLogout} >
                            Logout
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    if (!signInState.hasAccount && signInState.customerInfo.length > 0) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.shade}></div>
                {/* Logo is now a sibling of pageWrapperInner and absolutely positioned */}
                <div className={styles.logoWrapper}>
                    <img className={styles.logoImage} src={logo} alt="Basecamp Logo" />
                </div>
                <div className={styles.loginCard} >
                    <div className={styles.heading}>
                        <Title1>No user account found</Title1><p/> 
                        <Subtitle1>Speak to the administrator for the company you are trying to access for an invite.</Subtitle1><p/> 
                        <Button appearance='primary' size="medium" icon={<SignOutRegular />} onClick={handleLogout} >
                            Logout
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    if (signInState.customerInfo.length === 0) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.shade}></div>
                <div className={styles.logoWrapper}>
                    <img className={styles.logoImage} src={logo} alt="Basecamp Logo" />
                </div>
                <div className={styles.loginCard}>
                    <div className={styles.heading}>
                        <Title1>No workspace access</Title1><p/>
                        <Subtitle1>None of your Basecamp workspaces are linked to a Clarity customer. Speak to your administrator.</Subtitle1><p/>
                        <Button appearance='primary' size="medium" icon={<SignOutRegular />} onClick={handleLogout}>
                            Logout
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    if (signInState.customerInfo.length > 0 && !signInState.customerInfo.some(company => company.IsActive)) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.shade}></div>
                {/* Logo is now a sibling of pageWrapperInner and absolutely positioned */}
                <div className={styles.logoWrapper}>
                    <img className={styles.logoImage} src={logo} alt="Basecamp Logo" />
                </div>
                <div className={styles.loginCard} >
                    <div className={styles.heading}>
                        <Title1>Customer Account is Inactive</Title1>
                        {/* Redirect to buying a license again   */}
                    </div>
                </div>
            </div>
        )
    }

    return null;
};

export default SignUpFlow;